/**
 * Pre-flight compartido del permiso de micrófono.
 *
 * Por qué existe: el pipeline adquiere el mic recién en el `open` del WS
 * (`void mic.start()` — su rechazo sólo se loguea), así que un permiso
 * denegado o un mic inutilizable dejaban la sesión VIVA PERO MUDA sin ningún
 * aviso, facturando el opener TTS al vacío. fit (×2, lección M22), coach y
 * scout resolvieron eso con probes inline casi idénticos pero con
 * clasificación de errores duplicada y divergente; speak y spar no tenían
 * ninguno. Este helper es la única implementación: cada app decide la
 * POLÍTICA con el resultado (degradar la entrada a texto, bloquear con aviso,
 * etc.), no el mecanismo.
 *
 * Efecto secundario deliberado: el probe deja el permiso y el device
 * "calientes", así el `getUserMedia` real del pipeline resuelve en una
 * fracción del arranque frío.
 *
 * Además del pre-flight, el módulo expone la RECUPERACIÓN del permiso:
 * `requestMicPermission()` (mismo probe, pero disparado por un gesto del user
 * — ahí un prompt descartado vuelve a aparecer) y `watchMicPermission()` (para
 * cuando el browser ya bloqueó el sitio y el único camino son sus ajustes).
 * La política sigue siendo de la app: qué copy mostrar y si re-arma la voz en
 * la sesión viva o pide volver a entrar.
 *
 * Acá NO aplica la regla fail-fast del repo: un mic denegado/roto es entorno
 * del USER, no config nuestra — el resultado es un valor a decidir, no un
 * throw. Trade-off conocido: en iOS PWA cada acquire puede "chimear" (ver
 * nota histórica en MicCapture.ts) y el pre-flight suma un acquire por
 * arranque.
 */

/** Qué le pasa al mic cuando el pre-flight no da OK. */
export type MicIssue = 'denied' | 'unavailable';

export type MicPreflightResult =
  | { status: 'ok'; error?: undefined }
  | { status: MicIssue; error: unknown };

/**
 * Clasifica un error de `getUserMedia` / `MicCapture.start()`:
 * `'denied'` = el USER negó el permiso (NotAllowed/Security) → la copy debe
 * pedirle que lo active; `'unavailable'` = mic inutilizable (sin dispositivo
 * — NotFoundError —, ocupado por otra app — NotReadableError —,
 * OverconstrainedError, API ausente, etc.) → pedir "activá el permiso" sería
 * mentirle. Gotcha (lección de scout): en contexto inseguro (http://) el
 * browser tira SecurityError sin que el user haya negado nada — eso es
 * `'unavailable'`, no `'denied'`.
 */
export function classifyMicError(err: unknown): MicIssue {
  const name =
    (typeof DOMException !== 'undefined' && err instanceof DOMException) || err instanceof Error
      ? err.name
      : null;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    if (
      name === 'SecurityError' &&
      typeof window !== 'undefined' &&
      window.isSecureContext === false
    ) {
      return 'unavailable';
    }
    return 'denied';
  }
  return 'unavailable';
}

/**
 * Pide el mic, lo suelta al instante y clasifica el resultado. No toca el
 * singleton de `MicCapture` — el mic real lo abre el pipeline después (ya
 * sin prompt en browsers modernos).
 */
export async function preflightMic(): Promise<MicPreflightResult> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return {
      status: 'unavailable',
      error: new Error(
        typeof window !== 'undefined' && window.isSecureContext === false
          ? 'getUserMedia no disponible: contexto inseguro (se requiere HTTPS o localhost)'
          : 'navigator.mediaDevices.getUserMedia no disponible',
      ),
    };
  }
  try {
    const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of probe.getTracks()) track.stop();
    return { status: 'ok' };
  } catch (err) {
    return { status: classifyMicError(err), error: err };
  }
}

/**
 * Pide el permiso desde un GESTO del user (tap de un botón), para que la app
 * pueda ofrecer "dame el mic" en vez de mandarlo a los ajustes del browser.
 *
 * Es el MISMO probe que `preflightMic()` — el mecanismo del permiso es uno
 * solo — y existe como nombre aparte porque el momento cambia el resultado:
 * el pre-flight corre al entrar (sin gesto) y ahí un prompt DESCARTADO
 * (la X de Chrome, el "Don't Allow" de iOS) deja el permiso en 'prompt' y
 * rechaza igual; el mismo pedido bajo un gesto SÍ vuelve a promptear. Cuando
 * el browser ya bloqueó el sitio de verdad, esto rechaza sin prompt y la app
 * tiene que mandar al user a los ajustes (ver `watchMicPermission`).
 */
export async function requestMicPermission(): Promise<MicPreflightResult> {
  return preflightMic();
}

/**
 * Estado del permiso de mic según la Permissions API, o `null` si el browser no
 * lo expone (o no hay Permissions API).
 *
 * Para qué: un `getUserMedia` rechazado con `NotAllowedError` NO distingue
 * "el user descartó el prompt" (permiso sigue en `'prompt'`: el browser va a
 * volver a preguntar) de "el sitio está bloqueado" (`'denied'`: no pregunta
 * más), ni dice si el user lo habilitó a mano después (`'granted'`). speak lo
 * usa para dos cosas: no volver a pedir el mic solo cuando ya rebotó una vez,
 * salvo que el permiso esté concedido (ver `micAskMemory.ts`), y elegir el
 * discurso del aviso — sin esa diferencia la app le miente al user en uno de
 * los dos casos: mandarlo a los ajustes cuando bastaba tocar de nuevo, o
 * dejarlo tocando un botón que ya no puede promptear.
 *
 * Gotcha de Chrome (desktop y Android): descartar el prompt 3 veces para el
 * mismo origen dispara su auto-bloqueo ("permissions embargo", ~7 días), y
 * durante ese embargo esto devuelve `'denied'` aunque el user nunca haya
 * elegido "Bloquear". Es justo lo que la app necesita saber para cambiar de
 * discurso.
 */
export async function readMicPermission(): Promise<PermissionState | null> {
  if (typeof navigator === 'undefined' || !navigator.permissions) return null;
  try {
    const status = await navigator.permissions.query({ name: 'microphone' });
    return status.state;
  } catch {
    return null; // browser sin soporte para el permiso de mic
  }
}

/**
 * Escucha el permiso de mic por la Permissions API. Existe para el caso "el
 * browser ya bloqueó el sitio": ahí ningún `getUserMedia` promptea y el único
 * camino es habilitarlo a mano en los ajustes — este listener es lo que deja
 * que la app se recupere sola cuando el user lo hace, sin pedirle además que
 * vuelva a entrar.
 *
 * Best-effort a propósito: el permiso `'microphone'` está en el tipo pero no en
 * todos los browsers (Firefox tira `TypeError` al consultarlo), así que el
 * no-soporte degrada a un no-op silencioso — queda el reintento manual. No
 * aplica fail-fast por lo mismo que en el resto del módulo: es entorno del
 * user, no config nuestra. Devuelve la función para desuscribirse.
 */
export function watchMicPermission(onChange: (state: PermissionState) => void): () => void {
  let status: PermissionStatus | null = null;
  let cancelled = false;
  const handler = (): void => {
    if (status && !cancelled) onChange(status.state);
  };
  void (async () => {
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    try {
      const query = await navigator.permissions.query({ name: 'microphone' });
      if (cancelled) return;
      status = query;
      query.addEventListener('change', handler);
    } catch {
      // Browser sin soporte para el permiso de mic en la Permissions API.
    }
  })();
  return () => {
    cancelled = true;
    status?.removeEventListener('change', handler);
    status = null;
  };
}
